/**
 * ModernAdminsTab - Manage admin users and their permissions
 */
import React, { useEffect, useState } from 'react';
import { Settings, ShieldCheck, Plus, Trash2, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import AdminCard from './AdminCard';
import AdminCardActions from './AdminCardActions';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { adminAPI } from './AdminUtils';
import { useAdmin } from './AdminHooks';
import { cn } from '../../lib/utils';

const ALL_TABS = [
  { value: 'reservations', label: 'Réservations' },
  { value: 'orders', label: 'Commandes' },
  { value: 'tables', label: 'Tables' },
  { value: 'codes', label: 'Codes' },
  { value: 'events', label: 'Événements' },
  { value: 'media', label: 'Médias' },
  { value: 'clients', label: 'Clients' },
  { value: 'calendar', label: 'Calendrier' },
  { value: 'notifications', label: 'Emails' },
  { value: 'stats', label: 'Stats' },
  { value: 'admins', label: 'Admins' },
  { value: 'history', label: 'Historique' },
];

const RoleBadge = ({ role }) => {
  const map = {
    owner: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    admin: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };
  return <Badge className={cn(map[role] || 'bg-slate-700 text-slate-300')}>{role}</Badge>;
};

const ModernAdminsTab = () => {
  const { user } = useAdmin();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);

  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('admin');
  const [newAllowedTabs, setNewAllowedTabs] = useState([]);

  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('admin');
  const [editAllowedTabs, setEditAllowedTabs] = useState([]);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return window.matchMedia('(max-width: 768px)').matches; } catch { return false; }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const mq = window.matchMedia('(max-width: 768px)');
      const handler = (e) => setIsMobile(e.matches);
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else mq.onchange = handler;
      return () => {
        if (mq.removeEventListener) mq.removeEventListener('change', handler);
        else mq.onchange = null;
      };
    } catch {}
  }, []);
  const [viewMode, setViewMode] = useState('table');

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.get(`/admins`);
      setAdmins(res.data || []);
    } catch {
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAdmins(); }, []);

  const toggleCreateTab = (value) => {
    setNewAllowedTabs(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };
  const toggleEditTab = (value) => {
    setEditAllowedTabs(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const openEdit = (adm) => {
    setEditAdmin(adm);
    setEditName(adm.name || '');
    setEditRole(adm.role || 'admin');
    setEditAllowedTabs(adm.allowedTabs || []);
    setEditOpen(true);
  };

  const submitCreate = async () => {
    if (!newEmail || !newPassword) return;
    setCreating(true);
    try {
      await adminAPI.post(`/admins`, {
        email: newEmail,
        name: newName || '',
        password: newPassword,
        role: newRole,
        allowedTabs: newAllowedTabs,
      });
      setCreateOpen(false);
      setNewEmail('');
      setNewName('');
      setNewPassword('');
      setNewRole('admin');
      setNewAllowedTabs([]);
      await loadAdmins();
    } catch (e) {
      alert('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const submitEdit = async () => {
    if (!editAdmin) return;
    setUpdating(true);
    try {
      await adminAPI.put(`/admins/${editAdmin.id}`, {
        name: editName,
        role: editRole,
        allowedTabs: editAllowedTabs,
      });
      setEditOpen(false);
      setEditAdmin(null);
      await loadAdmins();
    } catch (e) {
      alert('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const deleteAdmin = async (adm) => {
    if (!window.confirm('Supprimer cet admin ?')) return;
    setDeleting(true);
    try {
      await adminAPI.delete(`/admins/${adm.id}`);
      await loadAdmins();
    } catch (e) {
      alert('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (user?.role !== 'owner') {
    return (
      <Card className="bg-slate-900/40 border-slate-700">
        <CardContent className="p-6">
          <p className="text-slate-300">Accès réservé au propriétaire.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestion des Admins</h2>
          <p className="text-slate-400">Créer, mettre à jour, supprimer des comptes admin et leurs permissions</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setCreateOpen(true)}
            className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Plus size={16} className="mr-2" />
            Nouvel Admin
          </Button>
          <div className="hidden rounded-lg border sm:flex bg-slate-900 border-slate-700">
            <Button
              size="sm"
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              onClick={() => setViewMode('table')}
              className={cn(
                "rounded-r-none",
                viewMode === 'table' && "bg-blue-600 text-white"
              )}
            >
              Tableau
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              onClick={() => setViewMode('cards')}
              className={cn(
                "rounded-l-none",
                viewMode === 'cards' && "bg-blue-600 text-white"
              )}
            >
              Cartes
            </Button>
          </div>
        </div>
      </div>

      <Card className="bg-slate-900/40 border-slate-700">
        <CardHeader>
          <CardTitle className="flex gap-2 items-center">
            <Settings size={18} />
            Admins
          </CardTitle>
          <CardDescription className="text-slate-400">
            {loading ? 'Chargement...' : `${admins.length} admin(s)`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {viewMode === 'table' ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Créé</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map(adm => (
                  <TableRow key={adm.id}>
                    <TableCell className="text-slate-300">{adm.name || '—'}</TableCell>
                    <TableCell className="text-white">{adm.email}</TableCell>
                    <TableCell><RoleBadge role={adm.role} /></TableCell>
                    <TableCell className="text-slate-300">
                      {(() => {
                        try {
                          const d = new Date(adm.createdAt);
                          return d.toLocaleString();
                        } catch {
                          return String(adm.createdAt || '—');
                        }
                      })()}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      <div className="flex flex-wrap gap-1">
                        {(adm.allowedTabs || []).map(t => (
                          <Badge key={t} className="bg-slate-700 text-slate-300 border-slate-600">{t}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                          onClick={() => openEdit(adm)}
                          title="Modifier"
                          aria-label="Modifier"
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="destructive"
                          size="icon"
                          onClick={() => deleteAdmin(adm)}
                          disabled={deleting}
                          title="Supprimer"
                          aria-label="Supprimer"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {admins.map((adm) => (
                <AdminCard key={adm.id}>
                  <CardHeader className="pb-3">
                    <div className="flex gap-2 items-center">
                      <ShieldCheck size={16} className="text-blue-400" />
                      <CardTitle className="text-lg text-white truncate">{adm.name || '—'}</CardTitle>
                      <RoleBadge role={adm.role} />
                    </div>
                    <div className="mt-1 text-sm text-slate-300">{adm.email}</div>
                    <div className="mt-1 text-xs text-slate-400">
                      {(() => {
                        try {
                          const d = new Date(adm.createdAt);
                          return d.toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' });
                        } catch {
                          return String(adm.createdAt || '—');
                        }
                      })()}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="p-3 min-w-0 rounded-lg border border-slate-700 bg-slate-800/40">
                        <div className="text-xs tracking-wide uppercase text-slate-400">Permissions</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(adm.allowedTabs || []).map(t => (
                            <Badge key={t} className="bg-slate-700 text-slate-300 border-slate-600">{t}</Badge>
                          ))}
                        </div>
                      </div>
                      <div className="p-3 min-w-0 rounded-lg border border-slate-700 bg-slate-800/40">
                        <div className="text-xs tracking-wide uppercase text-slate-400">Infos</div>
                        <div className="mt-1 text-sm text-slate-300">Rôle et permissions ci-contre</div>
                      </div>
                    </div>
                  </CardContent>
                  <AdminCardActions>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-slate-300 hover:text-white hover:bg-slate-700"
                      onClick={() => openEdit(adm)}
                      title="Modifier"
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteAdmin(adm)}
                      disabled={deleting}
                      className="text-slate-300 hover:text-red-400 hover:bg-red-500/10"
                      title="Supprimer"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </AdminCardActions>
                </AdminCard>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle>Créer un Admin</DialogTitle>
            <DialogDescription>Définissez les informations et les permissions</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Email</Label>
                <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email@example.com" />
              </div>
              <div>
                <Label>Nom</Label>
                <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nom" />
              </div>
              <div>
                <Label>Mot de passe</Label>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" />
              </div>
              <div>
                <Label>Rôle</Label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger className="w-full"><SelectValue placeholder="Choisir un rôle" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="owner">Propriétaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Permissions (onglets)</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 sm:grid-cols-3">
                {ALL_TABS.map(t => (
                  <label key={t.value} className="flex gap-2 items-center text-slate-300">
                    <Checkbox checked={newAllowedTabs.includes(t.value)} onCheckedChange={() => toggleCreateTab(t.value)} />
                    <span>{t.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white" onClick={() => setCreateOpen(false)}>Annuler</Button>
              <Button onClick={submitCreate} disabled={creating} className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                <Plus size={14} className="mr-1" />
                Créer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle>Modifier Admin</DialogTitle>
            <DialogDescription>Mettre à jour les permissions et le rôle</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Nom</Label>
                <Input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Nom" />
              </div>
              <div>
                <Label>Rôle</Label>
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger className="w-full"><SelectValue placeholder="Choisir un rôle" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="owner">Propriétaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Permissions (onglets)</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 sm:grid-cols-3">
                {ALL_TABS.map(t => (
                  <label key={t.value} className="flex gap-2 items-center text-slate-300">
                    <Checkbox checked={editAllowedTabs.includes(t.value)} onCheckedChange={() => toggleEditTab(t.value)} />
                    <span>{t.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white" onClick={() => setEditOpen(false)}>Annuler</Button>
              <Button onClick={submitEdit} disabled={updating} className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                <Pencil size={14} className="mr-1" />
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModernAdminsTab;
